import React, { useState, useEffect } from 'react';
import { FaQrcode, FaRedo, FaTimes } from 'react-icons/fa';
import { publicApi } from '../api/api';
import { trackFallbackPaymentSuccess } from '../utils/metaPageTracking';
import FallbackPaymentLogger from '../utils/fallbackPaymentLogger';

// OR Separator Component
const OrSeparator = () => (
  <div style={{
    display: "flex",
    alignItems: "center",
    margin: "16px 0",
    gap: "12px"
  }}>
    <div style={{
      flex: 1,
      height: "1px",
      background: "linear-gradient(to right, transparent, #ddd, transparent)"
    }}></div>
    <div style={{
      fontSize: "14px",
      fontWeight: 600,
      color: "#999",
      background: "white",
      padding: "0 8px"
    }}>
      OR
    </div>
    <div style={{
      flex: 1,
      height: "1px", 
      background: "linear-gradient(to left, transparent, #ddd, transparent)"
    }}></div>
  </div>
);

const FallbackPaymentModal = ({
  show,
  onClose,
  amount,
  orderId,
  fallbackInstructions,
  onRetry,
  onSuccess,
  triggerReason = 'unknown',  // Why modal was triggered: 'upi_failure' | 'gateway_error' | 'init_failure' | 'in_app_browser'
  errorInfo = null            // Original error details from payment failure
}) => {
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [paymentLinkInfo, setPaymentLinkInfo] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [qrCodeFailed, setQrCodeFailed] = useState(false);
  const [paymentLinkUrl, setPaymentLinkUrl] = useState(null);
  const [showFullQR, setShowFullQR] = useState(false);
  const [alreadyPaid, setAlreadyPaid] = useState(false);

  // Check if order is already paid before generating QR
  useEffect(() => {
    const checkAndGenerateQR = async () => {
      if (!show || !orderId || !amount) return;
      
      // Log modal opened with trigger reason from LandingPage
      FallbackPaymentLogger.modalOpened(orderId, amount, triggerReason, {
        errorInfo: errorInfo,
        browserInfo: {
          userAgent: navigator.userAgent,
          isInAppBrowser: /FBAN|FBAV|Instagram|Twitter|WhatsApp|TikTok|Telegram/i.test(navigator.userAgent)
        }
      });
      
      try {
        // Log status check
        FallbackPaymentLogger.statusCheck(orderId);
        
        // First check if order is already paid
        const statusResponse = await publicApi.get(`/payment/check-qr-status/${orderId}`);
        
        if (statusResponse.data.success && statusResponse.data.isCompleted) {
          // Order already paid - don't generate QR, trigger success
          console.log('Order already paid, skipping QR generation:', orderId);
          setAlreadyPaid(true);
          
          // Log already paid - QR skipped
          FallbackPaymentLogger.alreadyPaidSkipped(orderId, 'success');
          
          if (onSuccess) {
            onSuccess(statusResponse.data);
          }
          return;
        }
        
        // Order not paid yet - generate QR
        setAlreadyPaid(false);
        generateRazorpayQR();
      } catch (error) {
        // If status check fails, proceed with QR generation anyway
        console.warn('Failed to check order status, proceeding with QR generation:', error);
        FallbackPaymentLogger.error(orderId, { message: error.message }, { context: 'status_check_failed' });
        generateRazorpayQR();
      }
    };
    
    checkAndGenerateQR();
  }, [show, orderId, amount]);

  // Handle ESC key to close full screen QR
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showFullQR) {
        setShowFullQR(false);
      }
    };

    if (showFullQR) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent background scrolling when overlay is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [showFullQR]);

  // Poll for payment status
  useEffect(() => {
    let pollInterval;
    
    if (show && orderId && paymentLinkInfo) {
      // Start polling for payment status every 3 seconds
      pollInterval = setInterval(() => {
        checkPaymentStatus();
      }, 3000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [show, orderId, paymentLinkInfo]);

  const generateRazorpayQR = async () => {
    try {
      setQrLoading(true);
      setQrCodeFailed(false);

      const response = await publicApi.post('/payment/create-qr', {
        amount: amount,
        orderId: orderId,
        customerName: fallbackInstructions?.customerName || 'Customer',
        customerContact: fallbackInstructions?.customerContact || ''
      });

      if (response.data.success) {
        // Set QR code state based on backend response
        if (response.data.hasQrCode && response.data.qrCode) {
          setQrCodeUrl(response.data.qrCode);
          setQrCodeFailed(false);
          
          // Log QR generated successfully
          FallbackPaymentLogger.qrGenerated(
            orderId, 
            amount, 
            response.data.qrCodeId,
            response.data.paymentLinkId,
            response.data.paymentLinkUrl
          );
        } else {
          setQrCodeUrl(null);
          setQrCodeFailed(true);
        }

        // Set payment link state
        if (response.data.hasPaymentLink && response.data.paymentLinkUrl) {
          setPaymentLinkUrl(response.data.paymentLinkUrl);
        } else {
          setPaymentLinkUrl(null);
        }

        // Store the complete response for other operations
        setPaymentLinkInfo(response.data);
      } else {
        throw new Error(response.data.error || 'Failed to generate payment options');
      }
    } catch (error) {
      setQrCodeFailed(true);
      
      // Log QR generation failed
      FallbackPaymentLogger.qrGenerationFailed(orderId, amount, { message: error.message });
      
      // Try to create payment link as fallback
      try {
        const linkResponse = await publicApi.post('/payment/create-qr', {
          amount: amount,
          orderId: orderId,
          customerName: fallbackInstructions?.customerName || 'Customer',
          customerContact: fallbackInstructions?.customerContact || '',
          forcePaymentLink: true
        });
        
        if (linkResponse.data.success && linkResponse.data.paymentLinkUrl) {
          setPaymentLinkUrl(linkResponse.data.paymentLinkUrl);
          setPaymentLinkInfo(linkResponse.data);
          
          // Log payment link fallback
          FallbackPaymentLogger.paymentLinkFallback(
            orderId,
            amount,
            linkResponse.data.paymentLinkId,
            linkResponse.data.paymentLinkUrl
          );
        }
      } catch (linkError) {
        // Log fallback also failed
        FallbackPaymentLogger.error(orderId, { message: linkError.message }, { context: 'payment_link_fallback_failed' });
      }
    } finally {
      setQrLoading(false);
    }
  };

  const checkPaymentStatus = async (showLoader = false) => {
    try {
      // Only show loading indicator if explicitly requested (manual check)
      if (showLoader) {
        setCheckingPayment(true);
      }
      
      const response = await publicApi.get(`/payment/check-qr-status/${orderId}`);
      
      if (response.data.success && response.data.isCompleted) {
        // Log payment completed
        FallbackPaymentLogger.paymentCompleted(orderId, amount, {
          paymentId: response.data.paymentId,
          method: response.data.method
        });
        
        // Call success callback
        if (onSuccess) {
          onSuccess(response.data);
        }
        
        // NEW: Send Meta Conversions API event for Facebook attribution (configurable)
        try {
          await trackFallbackPaymentSuccess({
            orderId: orderId,
            amount: amount,
            currency: 'INR',
            userId: response.data.userId || null,
            customerEmail: response.data.email || null,
            customerPhone: response.data.phone || null
          });
          
          console.log('✅ Fallback Payment Meta tracking completed for order:', orderId);
        } catch (metaError) {
          // Non-blocking error - don't affect user experience
          console.warn('⚠️ Fallback Payment Meta tracking failed (non-blocking):', metaError);
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    } finally {
      // Only hide loading indicator if it was shown
      if (showLoader) {
        setCheckingPayment(false);
      }
    }
  };

  if (!show) return null;

  return (
    <>
      {/* QR Code CSS for better rendering */}
      <style>{`
        .qr-code-image {
          image-rendering: -moz-crisp-edges !important;
          image-rendering: -webkit-optimize-contrast !important;
          image-rendering: crisp-edges !important;
          image-rendering: pixelated !important;
          -ms-interpolation-mode: nearest-neighbor !important;
        }
        
        /* Prevent blur on high DPI displays */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .qr-code-image {
            image-rendering: -webkit-optimize-contrast !important;
            image-rendering: crisp-edges !important;
          }
        }

        /* Full screen QR animations */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.8);
          }
          to { 
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes pulse {
          0%, 100% { 
            transform: scale(1);
            opacity: 1;
          }
          50% { 
            transform: scale(1.02);
            opacity: 0.6;
          }
        }

        .qr-full-container {
          animation: scaleIn 0.3s ease-out;
        }

        .qr-clickable-hint:hover {
          animation: pulse 1s ease-in-out;
        }
      `}</style>

      <div
   
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.6)",
        zIndex: 10002,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          maxWidth: "380px",
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
          textAlign: "center",
          position: "relative"
        }}
      >
        {/* Close (X) Button - Top Right */}
        <button
          onClick={() => {
            FallbackPaymentLogger.modalClosed(orderId, false);
            onClose();
          }}
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            background: "transparent",
            border: "none",
            fontSize: "20px",
            color: "#666",
            cursor: "pointer",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
            zIndex: 10
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = "#f0f0f0";
            e.target.style.color = "#333";
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = "transparent";
            e.target.style.color = "#666";
          }}
        >
          <FaTimes size={16} />
        </button>

        {/* Header */}
        <div
          style={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#f57c00",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            paddingRight: "40px" // Make space for close button
          }}
        >
          <FaQrcode size={20} />
          Alternative Payment
        </div>

        {/* Order Details */}
        <div
          style={{
            background: "linear-gradient(135deg, #fff8f0 0%, #fff3e0 100%)",
            border: "1px solid #f57c00",
            borderRadius: "8px",
            padding: "10px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 2px 6px rgba(245, 124, 0, 0.1)"
          }}
        >
          <div style={{
            fontSize: "13px",
            color: "#666",
            fontFamily: "monospace"
          }}>
            Order <span style={{ color: "#333", fontWeight: 600 }}>#{orderId}</span>
          </div>
          <div style={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#f57c00"
          }}>
            ₹{amount}
          </div>
        </div>


        {/* 2. Razorpay QR Code Section - Show if loading, available, or failed with error message */}
        {(qrLoading || (!qrCodeFailed && qrCodeUrl) || qrCodeFailed) && (
          <div style={{ marginBottom: "20px" }}>
            <div style={{ fontSize: "14px", color: qrCodeFailed ? "#ff6b35" : "#4caf50", marginBottom: "8px", fontWeight: 600 }}>
              📱 Scan QR Code
            </div>
            <div
              style={{
                background: "#f8f9fa",
                border: `2px solid ${qrCodeFailed ? "#ff6b35" : "#4caf50"}`,
                borderRadius: "12px",
                padding: "16px",
                textAlign: "center"
              }}
            >
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
                Scan with any UPI app (PhonePe, GooglePay, Paytm)
              </div>
              <div 
                className="qr-clickable-hint"
                style={{ 
                  display: "inline-block",
                  padding: "8px",
                  background: "white",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  position: "relative",
                  overflow: "hidden"
                }}>
                {qrLoading ? (
                  <div style={{ 
                    width: "200px", 
                    height: "200px", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    color: "#666",
                    flexDirection: "column",
                    gap: "8px"
                  }}>
                    <div>🔄</div>
                    <div>Loading QR...</div>
                  </div>
                ) : qrCodeFailed ? (
                  <div style={{ 
                    width: "200px", 
                    height: "200px", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    color: "#ff6b35",
                    flexDirection: "column",
                    gap: "8px"
                  }}>
                    <div>❌</div>
                    <div>QR Code Failed</div>
                    <div style={{ fontSize: "10px" }}>Use Payment Link below</div>
                  </div>
                ) : (
                  <img 
                    src={qrCodeUrl} 
                    alt="Payment QR Code"
                    className="qr-code-image"
                    style={{ 
                      width: "200px", 
                      height: "200px",
                      display: "block",
                      imageRendering: "crisp-edges", 
                      filter: "contrast(1.2) brightness(1.1)", 
                      maxWidth: "100%",
                      objectFit: "contain",
                      border: "2px solid #fff",
                      borderRadius: "4px",
                      cursor: "pointer",
                      transition: "transform 0.2s ease"
                    }} 
                    onClick={() => setShowFullQR(true)}
                    onMouseOver={(e) => {
                      e.target.style.transform = "scale(1.05)";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = "scale(1)";
                    }}
                    onError={(e) => {
                      setQrCodeFailed(true);
                    }}
                  />
                )}
              </div>
              <div style={{ fontSize: "11px", color: qrCodeFailed ? "#ff6b35" : "#4caf50", marginTop: "8px" }}>
                {qrCodeFailed ? "⚠️ QR code generation failed" : "🔒 Direct UPI payment - No redirect required"}
              </div>
              
              {/* Click instruction */}
              {qrCodeUrl && !qrCodeFailed && (
                <div style={{ 
                  fontSize: "11px", 
                  color: "#007bff", 
                  marginTop: "6px",
                  fontWeight: "500"
                }}>
                  👆 Click QR code to view full screen
                </div>
              )}
            </div>
          </div>
        )}

        {/* OR Separator - Always show between QR and Payment Link */}
        <OrSeparator />

        {/* 3. Payment Link Button - Always Show */}
        <div style={{ marginBottom: "20px" }}>
          <div
            style={{
              background: "#f8f9fa",
              border: "2px solid #007bff",
              borderRadius: "12px",
              padding: "16px",
              textAlign: "center"
            }}
          >
            <button
              onClick={() => {
                if (paymentLinkUrl) {
                  window.open(paymentLinkUrl, '_blank');
                } else if (paymentLinkInfo && paymentLinkInfo.paymentLinkUrl) {
                  window.open(paymentLinkInfo.paymentLinkUrl, '_blank');
                }
              }}
              disabled={qrLoading || (!paymentLinkUrl && !paymentLinkInfo?.paymentLinkUrl)}
              style={{
                width: "100%",
                backgroundColor: qrLoading ? "#ffa726" : (paymentLinkUrl || paymentLinkInfo?.paymentLinkUrl) ? "#007bff" : "#f44336",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "14px 20px",
                fontSize: "16px",
                fontWeight: 600,
                cursor: paymentLinkUrl || paymentLinkInfo?.paymentLinkUrl ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                transition: "background-color 0.2s"
              }}
              onMouseOver={(e) => {
                if (paymentLinkUrl || paymentLinkInfo?.paymentLinkUrl) {
                  e.target.style.backgroundColor = "#0056b3";
                }
              }}
              onMouseOut={(e) => {
                if (paymentLinkUrl || paymentLinkInfo?.paymentLinkUrl) {
                  e.target.style.backgroundColor = "#007bff";
                }
              }}
            >
              {qrLoading ? (
                <>🔄 Generating Payment Options...</>
              ) : (paymentLinkUrl || paymentLinkInfo?.paymentLinkUrl) ? (
                <>💳 Pay via {paymentLinkInfo?.gateway === 'cashfree' ? 'Cashfree' : 'Razorpay'} Link</>
              ) : (
                <>❌ Payment Link Failed - Try Retry</>
              )}
            </button>
            <div style={{ fontSize: "11px", color: "#666", textAlign: "center", marginTop: "8px" }}>
              🔒 Opens {paymentLinkInfo?.gateway === 'cashfree' ? 'Cashfree' : 'Razorpay'} secure payment page
            </div>
          </div>
        </div>

        {/* OR Separator - Between Payment Link and Retry */}
        <OrSeparator />

        {/* Payment Status Message - Only show for manual checks */}
        {checkingPayment && (
          <div style={{
            background: "#e8f5e8",
            border: "1px solid #4caf50",
            borderRadius: "8px",
            padding: "12px",
            marginBottom: "16px",
            textAlign: "center"
          }}>
            <div style={{ color: "#4caf50", fontSize: "14px", fontWeight: 600 }}>
              🔍 Checking for payment...
            </div>
            <div style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}>
              Please wait while we verify your payment
            </div>
          </div>
        )}

        {/* Subtle auto-polling indicator */}
        {paymentLinkInfo && (
          <div style={{
            fontSize: "11px",
            color: "#999",
            textAlign: "center",
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px"
          }}>
            <div style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: "#4caf50",
              animation: "pulse 2s ease-in-out infinite"
            }}></div>
            Monitoring payment status...
          </div>
        )}

        {/* 4. Retry Payment Button */}
        <button
          onClick={() => {
            FallbackPaymentLogger.retryClicked(orderId);
            onRetry();
          }}
          style={{
            width: "100%",
            backgroundColor: "#f57c00",
            color: "white",
            border: "none",
            borderRadius: "8px",
            padding: "14px 20px",
            fontSize: "16px",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            transition: "background-color 0.2s"
          }}
          onMouseOver={(e) => (e.target.style.backgroundColor = "#e65100")}
          onMouseOut={(e) => (e.target.style.backgroundColor = "#f57c00")}
        >
          <FaRedo size={16} />
          Retry Payment
        </button>
      </div>
      </div>

      {/* Full-Page QR Code Overlay */}
      {showFullQR && qrCodeUrl && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.95)",
            zIndex: 20000, // Higher than modal
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            padding: "20px",
            animation: "fadeIn 0.3s ease"
          }}
          onClick={() => setShowFullQR(false)}
        >
          {/* QR Code Container */}
          <div
            className="qr-full-container"
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "30px",
              textAlign: "center",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              maxWidth: "90vw",
              maxHeight: "90vh"
            }}
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking QR
          >
            {/* Header */}
            <div style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#f57c00",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px"
            }}>
              <FaQrcode size={28} />
              Scan QR Code
            </div>
            
            {/* Amount Display */}
            <div style={{
              fontSize: "18px",
              color: "#4caf50",
              fontWeight: "600",
              marginBottom: "24px"
            }}>
              Pay ₹{amount}
            </div>

            {/* Large QR Code */}
            <div style={{
              display: "inline-block",
              padding: "20px",
              background: "#f8f9fa",
              borderRadius: "12px",
              border: "3px solid #4caf50"
            }}>
              <img 
                src={qrCodeUrl} 
                alt="Payment QR Code - Full Screen"
                style={{ 
                  width: "min(350px, 80vw)", 
                  height: "min(350px, 80vw)",
                  display: "block",
                  imageRendering: "crisp-edges",
                  imageRendering: "-webkit-optimize-contrast",
                  imageRendering: "pixelated",
                  objectFit: "contain"
                }} 
              />
            </div>

            {/* Instructions */}
            <div style={{ marginTop: "24px" }}>
              <div style={{
                fontSize: "16px",
                color: "#4caf50",
                fontWeight: "600",
                marginBottom: "12px"
              }}>
                📱 Open any UPI app and scan
              </div>
              
              <div style={{
                fontSize: "14px",
                color: "#666",
                lineHeight: "1.5",
                marginBottom: "20px"
              }}>
                PhonePe • GooglePay • Paytm • BHIM • Any Bank UPI App
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowFullQR(false)}
                style={{
                  backgroundColor: "#f57c00",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  padding: "12px 24px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "background-color 0.2s"
                }}
                onMouseOver={(e) => (e.target.style.backgroundColor = "#e65100")}
                onMouseOut={(e) => (e.target.style.backgroundColor = "#f57c00")}
              >
                Close Full Screen
              </button>
            </div>
          </div>

          {/* Background Click Hint */}
          <div style={{
            position: "absolute",
            bottom: "30px",
            left: "50%",
            transform: "translateX(-50%)",
            color: "rgba(255,255,255,0.7)",
            fontSize: "14px",
            textAlign: "center"
          }}>
            Click outside or press ESC to close
          </div>
        </div>
      )}
    </>
  );
};

export default FallbackPaymentModal;